import { useState } from "react";
import { Button } from "@/components/Base/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { Input } from "@/components/Base/Input";
import { Icon } from "@/components/Common/Icon";
import { Term, TerminologyLabels, TerminologyOptions } from "@/types/v2";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";

export interface EditTermDialogProps {
  termKey: TerminologyOptions;
  term: Term;
  onSave?: (term: Term) => void;
  onCancel?: () => void;
}

// Define the form schema using zod
const formSchema = z.object({
  singular: z.string().min(1, { message: "Singular form is required" }),
  plural: z.string().min(1, { message: "Plural form is required" }),
  acronym: z.string().min(1, { message: "Acronym is required" }),
});

type FormValues = z.infer<typeof formSchema>;

export const EditTermDialog = ({
  termKey,
  term,
  onSave,
  onCancel,
}: EditTermDialogProps) => {
  const [open, setOpen] = useState(false);

  // Initialize form with react-hook-form and zod validation
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      singular: term.singular,
      plural: term.plural,
      acronym: term.acronym,
    },
  });

  const handleSubmit = (values: FormValues) => {
    if (onSave) {
      onSave({
        singular: values.singular,
        plural: values.plural,
        acronym: values.acronym,
      });
    }
    setOpen(false);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    form.reset({
      singular: term.singular,
      plural: term.plural,
      acronym: term.acronym,
    });
    setOpen(false);
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="ml-2"
          onClick={() => {
            setOpen(true);
          }}
        >
          <Icon name={"editSquare"} className={"text-primary-foreground"} />
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[400px] p-0">
        <div className="flex h-[60px] w-full items-center justify-center bg-primary">
          <DialogTitle className="text-white">
            Edit {TerminologyLabels[termKey]} Terminology
          </DialogTitle>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <div className="px-5 py-2">
              <FormField
                control={form.control}
                name="singular"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="singularInput">Singular</FormLabel>
                    <FormControl>
                      <Input
                        id="singularInput"
                        type="text"
                        placeholder="Singular form"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="px-5 py-2">
              <FormField
                control={form.control}
                name="plural"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="pluralInput">Plural</FormLabel>
                    <FormControl>
                      <Input
                        id="pluralInput"
                        type="text"
                        placeholder="Plural form"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="px-5 py-2">
              <FormField
                control={form.control}
                name="acronym"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="acronymInput">Acronym</FormLabel>
                    <FormControl>
                      <Input
                        id="acronymInput"
                        type="text"
                        placeholder="Acronym"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col items-center space-y-3 px-5 py-4">
              <Button
                type="submit"
                variant="accent"
                className="h-[40px] w-full"
              >
                Save
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="w-full text-sm"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
